// Monthly Report — cross-cutting summary of the entire Command Center.
//
// Designed as the once-a-month read where decisions get made. The
// dashboards across /firm, /marketing, /crm, etc. are for "what is
// the current state"; this report is for "what changed and what
// should I do about it."
//
// Sections (top to bottom):
//   1. Executive Summary  — 5 bullets, scan-in-30-seconds
//   2. Financial          — revenue, net income, cash, deltas
//   3. Marketing          — spend %, cadence, audience, search, CAC, LTV
//   4. Pipeline           — new prospects, stage moves, won/lost
//   5. Clients            — net new, churn callouts
//   6. Insurance (BIA)    — minimal: renewals + bindings count
//   7. Watchlist          — items needing attention this month
//
// Data sources are existing contexts (FDC, ClientsContext,
// PipelineContext, ContentContext) plus parallel API calls to the
// AC / GSC / marketing summary endpoints. No new endpoints required.

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/apiFetch';
import { useFinancialData } from '../../context/FinancialDataContext';
import { useClients } from '../../context/ClientsContext';
import { usePipeline } from '../../context/PipelineContext';
import { useContent } from '../../context/ContentContext';
import { formatCurrency } from '../../utils/formatting';
import { getPillarLabel } from '../Marketing/pillars';
import { SERVICE_TIERS } from '../../utils/pipelineCalculations';

const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTH_LABELS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const BWM_REVENUE_LINES = ['subscription_fees', 'investment_management', 'tax_planning'];

function ymd(d) { return d.toISOString().slice(0, 10); }
function startOfMonth(year, monthIdx) { return new Date(Date.UTC(year, monthIdx, 1)); }
function endOfMonth(year, monthIdx) { return new Date(Date.UTC(year, monthIdx + 1, 0, 23, 59, 59)); }

// "Last completed month" — current month if we're past the 5th, else prior.
// Reasoning: by the 5th of a new month, last month's data has stabilized
// (QB sync done, bank reconciled). Before the 5th, the prior month is
// still being closed and the report would mostly show projections.
function defaultReportMonth() {
  const now = new Date();
  const useThisMonth = now.getDate() > 5;
  const target = useThisMonth ? now : new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: target.getFullYear(), monthIdx: target.getMonth() };
}

export default function MonthlyReport() {
  const [{ year, monthIdx }, setMonth] = useState(defaultReportMonth());
  // Bumping this re-fetches the API-backed sections (marketing, audience,
  // search). Financial + clients + pipeline come from contexts and update
  // automatically when their underlying data changes.
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastFetched, setLastFetched] = useState(new Date());

  function shiftMonth(delta) {
    const d = new Date(Date.UTC(year, monthIdx + delta, 1));
    setMonth({ year: d.getUTCFullYear(), monthIdx: d.getUTCMonth() });
    setLastFetched(new Date());
  }

  function refresh() {
    setRefreshTick(t => t + 1);
    setLastFetched(new Date());
  }

  const lastFetchedStr = lastFetched.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] px-5 py-4 print:shadow-none print:border print:border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888]">Bullogic Command Center · Monthly Report</div>
            <h1 className="text-[24px] font-bold text-navy mt-0.5">{MONTH_LABELS[monthIdx]} {year}</h1>
            <div className="text-[10px] text-[#aaa] mt-0.5 print:hidden">
              Marketing / audience / search data fetched at {lastFetchedStr}. Financial + pipeline + client data updates live.
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button onClick={() => shiftMonth(-1)} className="text-[12px] px-2 py-1 rounded-[3px] bg-bone/40 hover:bg-bone/60 text-[#666]">← Prev</button>
            <button onClick={() => shiftMonth(1)} className="text-[12px] px-2 py-1 rounded-[3px] bg-bone/40 hover:bg-bone/60 text-[#666]">Next →</button>
            <button onClick={refresh} className="text-[12px] font-bold px-3 py-1 rounded-[3px] bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20 ml-2">Refresh</button>
            <button onClick={() => window.print()} className="text-[12px] font-bold px-3 py-1 rounded-[3px] bg-bullogic-blue text-white hover:bg-bullogic-blue/90">Print / PDF</button>
          </div>
        </div>
      </div>

      {/* Sections */}
      <ReportBody year={year} monthIdx={monthIdx} refreshTick={refreshTick} />
    </div>
  );
}

// All section data is computed here so the watchlist can aggregate
// flags from every section in one pass.
function ReportBody({ year, monthIdx, refreshTick }) {
  const { editableByClass } = useFinancialData();
  const { clients } = useClients();
  const { prospects } = usePipeline();
  const { entries: content } = useContent();

  const [marketing, setMarketing] = useState(null);
  const [audience, setAudience] = useState(null);
  const [search, setSearch] = useState(null);
  const [calls, setCalls] = useState(null);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const errs = [];
    const force = refreshTick > 0 ? '&_=' + Date.now() : '';
    const ga4Path = `/api/gsc/ga4/calls?year=${year}&monthIdx=${monthIdx}`;
    Promise.allSettled([
      apiFetch(`/api/data/marketing/summary?year=${year}${force}`).then(r => r.json()),
      refreshTick > 0
        ? apiFetch('/api/data/ac/summary/refresh', { method: 'POST' }).then(r => r.json())
        : apiFetch('/api/data/ac/summary').then(r => r.json()),
      refreshTick > 0
        ? apiFetch('/api/gsc/report/refresh', { method: 'POST' }).then(r => r.json())
        : apiFetch('/api/gsc/report').then(r => r.json()),
      refreshTick > 0
        ? apiFetch(`/api/gsc/ga4/calls/refresh?year=${year}&monthIdx=${monthIdx}`, { method: 'POST' }).then(r => r.json())
        : apiFetch(ga4Path).then(r => r.json()),
    ]).then(([m, a, s, c]) => {
      if (cancelled) return;
      if (m.status === 'fulfilled') setMarketing(m.value); else errs.push(`Marketing: ${m.reason?.message || 'failed'}`);
      if (a.status === 'fulfilled') setAudience(a.value); else errs.push(`AC: ${a.reason?.message || 'failed'}`);
      if (s.status === 'fulfilled') setSearch(s.value); else errs.push(`GSC: ${s.reason?.message || 'failed'}`);
      if (c.status === 'fulfilled') setCalls(c.value); else errs.push(`GA4: ${c.reason?.message || 'failed'}`);
      setErrors(errs);
    });
    return () => { cancelled = true; };
  }, [year, monthIdx, refreshTick]);

  // Compute everything in one memo so watchlist + sections share data
  const data = useMemo(() => {
    return computeReportData({
      year, monthIdx, editableByClass, clients, prospects, content, marketing, audience, search, calls,
    });
  }, [year, monthIdx, editableByClass, clients, prospects, content, marketing, audience, search, calls]);

  return (
    <>
      <ExecSummarySection data={data} />
      <KeySignalsSection data={data} refreshTick={refreshTick} />
      <FinancialSection data={data} />
      <MarketingSection data={data} />
      <PipelineSection data={data} />
      <ClientsSection data={data} />
      <TrajectorySection data={data} />
      <InsuranceSection data={data} />
      {errors.length > 0 && (
        <div className="bg-rose/10 rounded-[4px] p-3 text-[11px] text-rose">
          <strong>Some sections couldn't load:</strong>
          <ul className="list-disc ml-5 mt-1">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
    </>
  );
}

// ─── Compute everything once ──────────────────────────────────────
function computeReportData({ year, monthIdx, editableByClass, clients, prospects, content, marketing, audience, search, calls }) {
  const monthStart = startOfMonth(year, monthIdx);
  const monthEnd = endOfMonth(year, monthIdx);
  const priorStart = startOfMonth(year, monthIdx - 1);
  const priorEnd = endOfMonth(year, monthIdx - 1);

  // ─── Financial (BWM-only, same as Budget dashboard) ───
  const bwmPL = editableByClass?.bwm;
  const bwmMonth = bwmPL?.months?.[MONTH_KEYS[monthIdx]];
  const bwmPriorMonth = monthIdx > 0 ? bwmPL?.months?.[MONTH_KEYS[monthIdx - 1]] : null;

  function bwmRev(m) {
    if (!m?.income) return 0;
    return BWM_REVENUE_LINES.reduce((s, k) => s + (Number(m.income[k]) || 0), 0);
  }
  function totalSpendCogsExp(m) {
    if (!m) return { cogs: 0, expenses: 0 };
    let cogs = 0, exp = 0;
    for (const v of Object.values(m.cogs || {})) cogs += Number(v) || 0;
    for (const v of Object.values(m.expenses || {})) exp += Number(v) || 0;
    return { cogs, expenses: exp };
  }

  const revenue = bwmRev(bwmMonth);
  const priorRevenue = bwmRev(bwmPriorMonth);
  const { cogs: thisCogs, expenses: thisExp } = totalSpendCogsExp(bwmMonth);
  const netIncome = revenue - thisCogs - thisExp;
  const { cogs: priorCogs, expenses: priorExp } = totalSpendCogsExp(bwmPriorMonth);
  const priorNetIncome = priorRevenue - priorCogs - priorExp;

  // ─── Marketing ───
  const marketingMonthSpend = marketing
    ? (marketing.softByMonth?.[monthIdx] || 0) + bwmHardForMonth(bwmMonth)
    : 0;
  const marketingPriorSpend = marketing
    ? (marketing.softByMonth?.[monthIdx - 1] || 0) + bwmHardForMonth(bwmPriorMonth)
    : 0;
  const ytdSpend = marketing
    ? (marketing.softByMonth || []).slice(0, monthIdx + 1).reduce((s, v) => s + v, 0)
      + (bwmPL?.months ? MONTH_KEYS.slice(0, monthIdx + 1).reduce((s, mk) => s + bwmHardForMonth(bwmPL.months[mk]), 0) : 0)
    : 0;
  const ytdRevenue = bwmPL?.months
    ? MONTH_KEYS.slice(0, monthIdx + 1).reduce((s, mk) => s + bwmRev(bwmPL.months[mk]), 0)
    : 0;
  const ytdSpendPct = ytdRevenue > 0 ? ytdSpend / ytdRevenue : 0;
  const goalPct = marketing?.goalPercent || 0.15;

  // Anchors published this month
  const publishedThisMonth = (content || []).filter(e => {
    if (e.status !== 'published' || !e.publishDate) return false;
    const d = new Date(e.publishDate);
    return d >= monthStart && d <= monthEnd;
  });
  const publishedPriorMonth = (content || []).filter(e => {
    if (e.status !== 'published' || !e.publishDate) return false;
    const d = new Date(e.publishDate);
    return d >= priorStart && d <= priorEnd;
  });
  const queueDepth = (content || []).filter(e =>
    e.status === 'outlined' || e.status === 'drafted' || e.status === 'scheduled'
  ).length;

  // Audience (annual numbers from AC; we approximate "this month" from the 30-day delta)
  const audienceTotal = audience?.total || null;
  const audience30dNew = audience?.monthly?.new || 0;
  const audience30dDelta = audience?.monthly?.delta || 0;

  // Search (28-day rolling — closest analog to monthly)
  const searchClicks = search?.summary?.clicks || 0;
  const searchImpressions = search?.summary?.impressions || 0;

  // ─── Pipeline ───
  // BWM only for CAC/LTV alignment
  const isBwm = (p) => {
    const div = p.division || SERVICE_TIERS[p.serviceTier]?.division;
    return div === 'bwm';
  };
  const bwmProspects = (prospects || []).filter(isBwm);

  function inMonthRange(dateStr, start, end) {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= start && d <= end;
  }

  const newProspectsThisMonth = bwmProspects.filter(p => inMonthRange(p.createdDate, monthStart, monthEnd));
  const wonThisMonth = bwmProspects.filter(p => p.outcome === 'won' && inMonthRange(p.closedDate, monthStart, monthEnd));
  const lostThisMonth = bwmProspects.filter(p => p.outcome === 'lost' && inMonthRange(p.closedDate, monthStart, monthEnd));
  const wonPriorMonth = bwmProspects.filter(p => p.outcome === 'won' && inMonthRange(p.closedDate, priorStart, priorEnd));
  const lostPriorMonth = bwmProspects.filter(p => p.outcome === 'lost' && inMonthRange(p.closedDate, priorStart, priorEnd));
  const newProspectsPriorMonth = bwmProspects.filter(p => inMonthRange(p.createdDate, priorStart, priorEnd));

  // Loss reasons summary
  const lossReasons = {};
  for (const p of lostThisMonth) {
    const r = p.lossReason || 'unspecified';
    lossReasons[r] = (lossReasons[r] || 0) + 1;
  }

  // Stage transitions this month + prior — count entries in stageHistory
  // whose enteredDate falls in the window, excluding initial creation.
  let stageMovesThisMonth = 0;
  let stageMovesPriorMonth = 0;
  for (const p of bwmProspects) {
    const h = Array.isArray(p.stageHistory) ? p.stageHistory : [];
    for (let i = 1; i < h.length; i++) {
      if (inMonthRange(h[i].enteredDate, monthStart, monthEnd)) stageMovesThisMonth++;
      if (inMonthRange(h[i].enteredDate, priorStart, priorEnd)) stageMovesPriorMonth++;
    }
  }

  // Stalled prospects — open BWM in active stages with stageHistory
  // showing >30 days since last advance
  const stalled = [];
  const now = new Date();
  for (const p of bwmProspects) {
    if (p.outcome) continue;
    if (!['discovery-complete', 'strategy-scheduled', 'strategy-complete', 'proposal'].includes(p.stage)) continue;
    const h = Array.isArray(p.stageHistory) ? p.stageHistory : [];
    const cur = h.find(x => x?.stageId === p.stage && !x?.exitedDate);
    if (!cur?.enteredDate) continue;
    const days = Math.floor((now - new Date(cur.enteredDate)) / (1000 * 60 * 60 * 24));
    if (days > 30) stalled.push({ name: p.name, stage: p.stage, days });
  }

  // ─── Clients (BWM only) ───
  const bwmClients = (clients || []).filter(c => c.division === 'bwm');
  const addedThisMonth = bwmClients.filter(c => inMonthRange(c.startDate, monthStart, monthEnd));
  const churnedThisMonth = bwmClients.filter(c => c.status === 'Churned' && inMonthRange(c.churnedDate, monthStart, monthEnd));
  const addedPriorMonth = bwmClients.filter(c => inMonthRange(c.startDate, priorStart, priorEnd));
  const churnedPriorMonth = bwmClients.filter(c => c.status === 'Churned' && inMonthRange(c.churnedDate, priorStart, priorEnd));
  const activeBwm = bwmClients.filter(c => c.status === 'Active');
  const netChange = addedThisMonth.length - churnedThisMonth.length;
  const priorNetChange = addedPriorMonth.length - churnedPriorMonth.length;

  // ─── CAC / LTV (annual context, surfaced in report) ───
  let cac = null, ltv = null, ltvCac = null;
  if (marketing && marketing.wonCount > 0) {
    cac = Math.round(ytdSpend / marketing.wonCount);
  }
  if (activeBwm.length > 0) {
    const yrs = 2; // matches LTV default
    let total = 0;
    for (const c of activeBwm) {
      const monthlyAnnualized = (Number(c.monthlyRevenue) || 0) * 12;
      const annualBase = monthlyAnnualized || (Number(c.annualRevenue) || 0);
      const investmentRecurring = Number(c.investmentFeeAnnual) || 0;
      const taxPlanOneTime = Number(c.taxPlanFee) || 0;
      total += (annualBase + investmentRecurring) * yrs + taxPlanOneTime;
    }
    ltv = Math.round(total / activeBwm.length);
    if (cac) ltvCac = ltv / cac;
  }

  // ─── Watchlist ───
  const watchlist = [];
  if (publishedThisMonth.length === 0 && monthIdx === new Date().getMonth() && year === new Date().getFullYear()) {
    watchlist.push({ severity: 'red', text: 'No anchors published this month yet — cadence at risk' });
  }
  if (queueDepth === 0) {
    watchlist.push({ severity: 'red', text: 'Queue depth = 0 — schedule a surge block immediately' });
  } else if (queueDepth === 1) {
    watchlist.push({ severity: 'amber', text: 'Queue depth = 1 — schedule a surge block within 7 days' });
  }
  if (ytdSpendPct < goalPct * 0.5) {
    watchlist.push({ severity: 'amber', text: `Marketing spend at ${(ytdSpendPct * 100).toFixed(1)}% YTD vs ${(goalPct * 100).toFixed(0)}% goal — well under target` });
  }
  if (ltvCac !== null && ltvCac < 1) {
    watchlist.push({ severity: 'red', text: `LTV:CAC = ${ltvCac.toFixed(1)}x — losing money on each acquired client` });
  }
  if (churnedThisMonth.length > 0) {
    watchlist.push({ severity: 'amber', text: `${churnedThisMonth.length} BWM client${churnedThisMonth.length > 1 ? 's' : ''} churned: ${churnedThisMonth.map(c => c.name).join(', ')}` });
  }
  if (stalled.length > 0) {
    watchlist.push({ severity: 'amber', text: `${stalled.length} prospect${stalled.length > 1 ? 's' : ''} stalled >30 days in active stage: ${stalled.slice(0, 5).map(s => s.name).join(', ')}${stalled.length > 5 ? '…' : ''}` });
  }
  if (lostThisMonth.length > wonThisMonth.length && lostThisMonth.length >= 2) {
    watchlist.push({ severity: 'amber', text: `Won ${wonThisMonth.length} but lost ${lostThisMonth.length} this month — review loss reasons` });
  }

  // ─── Executive summary bullets ───
  const summary = [];
  summary.push(`${publishedThisMonth.length} anchor${publishedThisMonth.length === 1 ? '' : 's'} published${publishedThisMonth.length >= 1 ? ' ✓' : ''}`);
  summary.push(`Marketing spend ${(ytdSpendPct * 100).toFixed(1)}% of revenue YTD (goal ${(goalPct * 100).toFixed(0)}%)`);
  summary.push(`${addedThisMonth.length} BWM client${addedThisMonth.length === 1 ? '' : 's'} added, ${churnedThisMonth.length} churned (net ${netChange >= 0 ? '+' : ''}${netChange})`);
  summary.push(`${wonThisMonth.length} closed-won this month, ${newProspectsThisMonth.length} new BWM prospect${newProspectsThisMonth.length === 1 ? '' : 's'} added`);
  if (ltvCac !== null) {
    summary.push(`LTV:CAC ratio ${ltvCac.toFixed(1)}x · LTV ${formatCurrency(ltv)} · CAC ${formatCurrency(cac)}`);
  } else {
    summary.push('LTV:CAC pending first BWM closed-won');
  }

  // ─── 6-month trajectory series ───
  // For each of the trailing 6 months (including current), compute the
  // headline metrics. Used by the Trajectory section's mini sparklines
  // at the bottom of the report. All BWM-scoped to match the rest.
  const trajectory = (() => {
    const N = 6;
    const labels = [];
    const revenueSeries = [];
    const spendPctSeries = [];
    const anchorsSeries = [];
    const wonSeries = [];
    const newProspectsSeries = [];

    for (let i = N - 1; i >= 0; i--) {
      const d = new Date(Date.UTC(year, monthIdx - i, 1));
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth();
      const mk = MONTH_KEYS[m];
      const mStart = startOfMonth(y, m);
      const mEnd = endOfMonth(y, m);
      labels.push(MONTH_LABELS[m].slice(0, 3));

      // Revenue (BWM) — only available for the year FDC has loaded (2026).
      // For months in different years, fall back to 0 (this is acceptable;
      // sparklines will show only the months we have).
      const monthData = (y === year) ? bwmPL?.months?.[mk] : null;
      revenueSeries.push(monthData ? bwmRev(monthData) : 0);

      // YTD spend pct ENDING at this month (includes Jan…this month soft + hard)
      let cumSoft = 0, cumHard = 0, cumRev = 0;
      if (y === year && bwmPL?.months) {
        for (let mi = 0; mi <= m; mi++) {
          const m2 = bwmPL.months[MONTH_KEYS[mi]];
          cumHard += bwmHardForMonth(m2);
          cumRev += bwmRev(m2);
        }
        if (marketing?.softByMonth) {
          for (let mi = 0; mi <= m; mi++) cumSoft += marketing.softByMonth[mi] || 0;
        }
      }
      const cumSpend = cumSoft + cumHard;
      spendPctSeries.push(cumRev > 0 ? cumSpend / cumRev : 0);

      // Anchors published in this month
      const anchorCount = (content || []).filter(e => {
        if (e.status !== 'published' || !e.publishDate) return false;
        const dt = new Date(e.publishDate);
        return dt >= mStart && dt <= mEnd;
      }).length;
      anchorsSeries.push(anchorCount);

      // Closed-won in this month (BWM)
      const wonCount = bwmProspects.filter(p => p.outcome === 'won' && inMonthRange(p.closedDate, mStart, mEnd)).length;
      wonSeries.push(wonCount);

      // New prospects in this month (BWM)
      const newCount = bwmProspects.filter(p => inMonthRange(p.createdDate, mStart, mEnd)).length;
      newProspectsSeries.push(newCount);
    }

    return { labels, revenueSeries, spendPctSeries, anchorsSeries, wonSeries, newProspectsSeries };
  })();

  return {
    year, monthIdx,
    summary, watchlist, trajectory,
    financial: {
      revenue, priorRevenue,
      netIncome, priorNetIncome,
      cogs: thisCogs, expenses: thisExp,
    },
    marketing: {
      monthSpend: marketingMonthSpend,
      priorSpend: marketingPriorSpend,
      ytdSpend, ytdRevenue, ytdSpendPct, goalPct,
      anchorsPublished: publishedThisMonth.length,
      anchorsPublishedPrior: publishedPriorMonth.length,
      anchorsList: publishedThisMonth,
      queueDepth,
      audienceTotal, audience30dNew, audience30dDelta,
      searchClicks, searchImpressions,
      cac, ltv, ltvCac,
      callsThisMonth: calls?.thisMonth ?? null,
      callsPriorMonth: calls?.priorMonth ?? null,
      callsByContent: calls?.byContent || [],
      callsConfigured: calls?.configured !== false,
    },
    pipeline: {
      newProspects: newProspectsThisMonth.length,
      newProspectsPrior: newProspectsPriorMonth.length,
      newProspectsList: newProspectsThisMonth,
      stageMoves: stageMovesThisMonth,
      stageMovesPrior: stageMovesPriorMonth,
      won: wonThisMonth.length, wonList: wonThisMonth, wonPrior: wonPriorMonth.length,
      lost: lostThisMonth.length, lostList: lostThisMonth, lostPrior: lostPriorMonth.length,
      lossReasons,
      stalled,
    },
    clients: {
      activeCount: activeBwm.length,
      added: addedThisMonth.length, addedPrior: addedPriorMonth.length, addedList: addedThisMonth,
      churned: churnedThisMonth.length, churnedPrior: churnedPriorMonth.length, churnedList: churnedThisMonth,
      netChange, priorNetChange,
    },
  };
}

function bwmHardForMonth(m) {
  if (!m) return 0;
  return (Number(m.cogs?.referral_fees) || 0) + (Number(m.expenses?.advertising_promotional) || 0);
}

// Compute a clean delta ratio for showing "X% vs prior". Returns null
// when prior is 0 (avoids meaningless +∞%) and when current+prior are
// both 0 (no change worth reporting). Returns a Number ratio that the
// Stat component formats as a percentage.
function pctDelta(current, prior) {
  if (prior === undefined || prior === null) return null;
  if (prior === 0 && current === 0) return null;
  if (prior === 0) return null;
  return (current - prior) / Math.abs(prior);
}

// ─── Section renderers ────────────────────────────────────────────

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 print:shadow-none print:border print:border-gray-200 print:break-inside-avoid">
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888]">{title}</div>
      {subtitle && <div className="text-[10px] text-[#aaa] mt-0.5">{subtitle}</div>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function ExecSummarySection({ data }) {
  return (
    <SectionCard title="Executive Summary">
      <ul className="space-y-1 text-[14px] text-navy">
        {data.summary.map((s, i) => <li key={i} className="flex gap-2"><span className="text-bullogic-blue">•</span><span>{s}</span></li>)}
      </ul>
    </SectionCard>
  );
}

// Unified Key Signals: rule-based items + AI-generated insights in one
// section. Rule items render as collapsed one-liners (no expand needed —
// they're self-explanatory). AI items render expandable with a body +
// recommendation. Severity normalization: rule 'amber' maps to 'yellow'
// to align with the AI's red/yellow/green scheme.
//
// Auto-runs the AI analysis on mount + on every refreshTick bump (i.e.
// when the user clicks the report's Refresh button). Caches per-month in
// localStorage so re-loads don't trigger fresh API calls.
const SIG_SEVERITY_ORDER = { red: 0, yellow: 1, green: 2 };
const SIG_STYLE = {
  red:    { dot: 'bg-rose',         label: 'ATTENTION', labelBg: 'bg-rose/10 text-rose' },
  yellow: { dot: 'bg-amber-500',    label: 'MONITOR',   labelBg: 'bg-amber-100 text-amber-700' },
  green:  { dot: 'bg-deep-teal',    label: 'ON TRACK',  labelBg: 'bg-deep-teal/10 text-deep-teal' },
};

function normalizeSeverity(s) {
  if (s === 'amber') return 'yellow';
  if (!s) return 'yellow';
  return s;
}

function KeySignalsSection({ data, refreshTick }) {
  const cacheKey = `bcc_report_signals_${data.year}_${String(data.monthIdx).padStart(2, '0')}`;

  const [aiSignals, setAiSignals] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) return JSON.parse(raw).signals || [];
    } catch (e) { /* ignore */ }
    return null; // null = haven't loaded yet
  });
  const [aiCachedAt, setAiCachedAt] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) return JSON.parse(raw).timestamp || null;
    } catch (e) { /* ignore */ }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [activeFilters, setActiveFilters] = useState({ red: true, yellow: true, green: true });

  // When month changes: load cache if present, otherwise fetch fresh.
  useEffect(() => {
    setExpanded({});
    setError(null);
    let cached = null;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) cached = JSON.parse(raw);
    } catch (e) { /* ignore */ }
    if (cached?.signals) {
      setAiSignals(cached.signals);
      setAiCachedAt(cached.timestamp || null);
    } else {
      setAiSignals(null);
      setAiCachedAt(null);
      runAnalysis(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // When the user clicks the report's Refresh button (refreshTick bumps
  // past 0), force a fresh AI analysis. Initial mount has refreshTick === 0
  // so this path is skipped — initial load is handled by the cacheKey
  // effect above.
  useEffect(() => {
    if (refreshTick > 0) runAnalysis(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  async function runAnalysis(force) {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/claude/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'monthly-report',
          reportData: data,
          ruleWatchlist: data.watchlist,
        }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'AI analysis failed');
      const signals = (j.observations || []).map(o => ({
        ...o,
        severity: normalizeSeverity(o.severity),
        source: 'ai',
      }));
      setAiSignals(signals);
      setAiCachedAt(j.timestamp);
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ signals, timestamp: j.timestamp }));
      } catch (e) { /* ignore quota */ }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Combine rule items + AI items into a single sorted list
  const ruleItems = (data.watchlist || []).map((w, i) => ({
    severity: normalizeSeverity(w.severity),
    title: w.text,
    source: 'rule',
    id: `rule-${i}`,
  }));
  const aiItems = (aiSignals || []).map((s, i) => ({
    severity: normalizeSeverity(s.severity),
    title: s.title,
    observation: s.observation,
    recommendation: s.recommendation,
    source: 'ai',
    id: `ai-${i}`,
  }));
  const all = [...ruleItems, ...aiItems].sort(
    (a, b) => (SIG_SEVERITY_ORDER[a.severity] ?? 9) - (SIG_SEVERITY_ORDER[b.severity] ?? 9)
  );
  const visible = all.filter(s => activeFilters[s.severity]);

  const counts = all.reduce((acc, s) => { acc[s.severity] = (acc[s.severity] || 0) + 1; return acc; }, {});

  function toggleFilter(sev) {
    setActiveFilters(prev => ({ ...prev, [sev]: !prev[sev] }));
  }
  function toggleExpand(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 print:shadow-none print:border print:border-gray-200 print:break-inside-avoid">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888]">Key Signals</div>
          <div className="text-[10px] text-[#aaa] mt-0.5">
            Rule-based flags + AI-generated insights · {loading ? 'analyzing…' : aiCachedAt ? `analysis from ${new Date(aiCachedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : 'no analysis yet'}
          </div>
        </div>
        {/* Severity filter chips */}
        {all.length > 0 && (
          <div className="flex items-center gap-1.5 print:hidden">
            {['red', 'yellow', 'green'].map(sev => (
              counts[sev] > 0 && (
                <button
                  key={sev}
                  onClick={() => toggleFilter(sev)}
                  className={`px-1.5 py-0.5 text-[10px] font-bold rounded-[3px] transition-colors ${
                    activeFilters[sev] ? SIG_STYLE[sev].labelBg : 'bg-gray-100 text-gray-400 line-through'
                  }`}
                >
                  {counts[sev]} {SIG_STYLE[sev].label.toLowerCase()}
                </button>
              )
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="text-[11px] text-rose mt-2">AI analysis failed: {error}. Rule-based flags still shown below.</div>
      )}

      {visible.length === 0 ? (
        <div className="text-[12px] text-[#888] mt-3">
          {loading ? 'Generating AI analysis…' :
           all.length === 0 ? 'No signals this month — clean read.' :
           'No signals match the active filters.'}
        </div>
      ) : (
        <ul className="space-y-2 mt-3">
          {visible.map((s) => {
            const style = SIG_STYLE[s.severity];
            const isExpandable = s.source === 'ai';
            const isExpanded = expanded[s.id];
            return (
              <li key={s.id} className="border-l-2 pl-3 py-1" style={{ borderLeftColor: getColor(s.severity) }}>
                <div
                  className={`flex items-start gap-2 ${isExpandable ? 'cursor-pointer' : ''}`}
                  onClick={isExpandable ? () => toggleExpand(s.id) : undefined}
                >
                  <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${style.dot}`} />
                  <span className="flex-1 text-[13px] text-navy leading-snug">{s.title}</span>
                  {isExpandable && (
                    <span className="text-[10px] text-[#aaa] shrink-0 mt-0.5">
                      {isExpanded ? '−' : '+'}
                    </span>
                  )}
                </div>
                {isExpandable && isExpanded && (
                  <div className="mt-2 ml-4 space-y-1.5 text-[12px]">
                    {s.observation && <div className="text-[#444]">{s.observation}</div>}
                    {s.recommendation && (
                      <div className="text-[#444]">
                        <strong className="text-navy">Action:</strong> {s.recommendation}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function getColor(sev) {
  if (sev === 'red') return '#FF6E83';
  if (sev === 'yellow') return '#F59E0B';
  if (sev === 'green') return '#006D78';
  return '#888';
}

function FinancialSection({ data }) {
  const f = data.financial;
  const revDelta = f.priorRevenue > 0 ? (f.revenue - f.priorRevenue) / f.priorRevenue : null;
  const niDelta = f.priorNetIncome !== 0 ? (f.netIncome - f.priorNetIncome) / Math.abs(f.priorNetIncome) : null;
  return (
    <SectionCard title="Financial — BWM" subtitle="Subscription + investment management + tax planning · vs prior month">
      <div className="grid grid-cols-3 gap-4 text-[13px]">
        <Stat label="Revenue" value={formatCurrency(f.revenue)} delta={revDelta} />
        <Stat label="Net Income" value={formatCurrency(f.netIncome)} delta={niDelta} />
        <Stat label="COGS + Expenses" value={formatCurrency(f.cogs + f.expenses)} subtitle={`COGS ${formatCurrency(f.cogs)} · Exp ${formatCurrency(f.expenses)}`} />
      </div>
    </SectionCard>
  );
}

function MarketingSection({ data }) {
  const m = data.marketing;
  const ratioColor =
    m.ltvCac === null            ? 'text-[#999]' :
    m.ltvCac >= 3 && m.ltvCac < 5 ? 'text-deep-teal' :
    m.ltvCac >= 5                 ? 'text-bullogic-blue' :
    m.ltvCac < 1                  ? 'text-rose' :
                                    'text-amber-600';
  return (
    <SectionCard title="Marketing" subtitle="Cadence · spend · CAC · LTV">
      <div className="grid grid-cols-3 gap-4 text-[13px]">
        <Stat label="Spend % YTD" value={`${(m.ytdSpendPct * 100).toFixed(1)}%`} subtitle={`Goal ${(m.goalPct * 100).toFixed(0)}%`} accent={m.ytdSpendPct >= m.goalPct ? 'text-deep-teal' : 'text-navy'} />
        <Stat label="Anchors This Month" value={m.anchorsPublished} delta={pctDelta(m.anchorsPublished, m.anchorsPublishedPrior)} subtitle={`Prior: ${m.anchorsPublishedPrior} · Queue: ${m.queueDepth}`} />
        <Stat label="LTV:CAC" value={m.ltvCac === null ? '—' : `${m.ltvCac.toFixed(1)}x`} subtitle={m.ltv ? `LTV ${formatCurrency(m.ltv)} · CAC ${formatCurrency(m.cac)}` : 'awaiting first win'} accent={ratioColor} />
        <Stat label="Audience (Newsletter)" value={m.audienceTotal != null ? m.audienceTotal.toLocaleString() : '—'} subtitle={`${m.audience30dNew >= 0 ? '+' : ''}${m.audience30dNew} last 30 days`} />
        <Stat label="Search Clicks (28d)" value={m.searchClicks?.toLocaleString() || '—'} subtitle={`${m.searchImpressions?.toLocaleString() || '—'} impressions`} />
        <Stat label="Spend This Month" value={formatCurrency(m.monthSpend)} delta={pctDelta(m.monthSpend, m.priorSpend)} subtitle={`Prior: ${formatCurrency(m.priorSpend)}`} />
        <Stat
          label="Discovery Calls Booked"
          value={m.callsConfigured === false ? '—' : (m.callsThisMonth ?? '—')}
          delta={m.callsConfigured === false ? null : pctDelta(m.callsThisMonth, m.callsPriorMonth)}
          subtitle={m.callsConfigured === false ? 'GA4 not connected' : `Prior: ${m.callsPriorMonth ?? '—'}`}
          accent={m.callsThisMonth > 0 ? 'text-deep-teal' : 'text-navy'}
        />
      </div>

      {m.callsByContent.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Discovery calls by source (this month)</div>
          <ul className="space-y-1 text-[12px]">
            {m.callsByContent.slice(0, 8).map((c, i) => (
              <li key={i} className="flex justify-between">
                <span className="text-navy font-mono text-[11px]">{c.utmContent}</span>
                <span className="text-[#666] tabular-nums">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {m.anchorsList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Anchors published this month</div>
          <ul className="space-y-1 text-[12px]">
            {m.anchorsList.map(e => (
              <li key={e.id} className="flex justify-between">
                <span className="text-navy truncate">{e.title}</span>
                <span className="text-[#888]">{getPillarLabel(e.pillar)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

function PipelineSection({ data }) {
  const p = data.pipeline;
  return (
    <SectionCard title="Pipeline — BWM" subtitle="New prospects · stage moves · closures">
      <div className="grid grid-cols-4 gap-4 text-[13px]">
        <Stat label="New Prospects" value={p.newProspects} delta={pctDelta(p.newProspects, p.newProspectsPrior)} subtitle={`Prior: ${p.newProspectsPrior}`} />
        <Stat label="Stage Moves" value={p.stageMoves} delta={pctDelta(p.stageMoves, p.stageMovesPrior)} subtitle={`Prior: ${p.stageMovesPrior}`} />
        <Stat label="Closed Won" value={p.won} delta={pctDelta(p.won, p.wonPrior)} subtitle={`Prior: ${p.wonPrior}`} accent={p.won > 0 ? 'text-deep-teal' : 'text-navy'} />
        <Stat label="Closed Lost" value={p.lost} delta={pctDelta(p.lost, p.lostPrior)} subtitle={`Prior: ${p.lostPrior}`} accent={p.lost > 0 ? 'text-rose' : 'text-[#888]'} />
      </div>

      {p.wonList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Closed-won this month</div>
          <ul className="space-y-1 text-[12px]">
            {p.wonList.map(prospect => (
              <li key={prospect.id} className="flex justify-between">
                <span className="text-navy truncate">{prospect.name}</span>
                <span className="text-[#888]">{prospect.serviceTier || prospect.source || ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Object.keys(p.lossReasons).length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Loss reasons</div>
          <ul className="space-y-1 text-[12px]">
            {Object.entries(p.lossReasons).map(([reason, count]) => (
              <li key={reason} className="flex justify-between">
                <span className="text-navy">{reason}</span>
                <span className="text-[#888] tabular-nums">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.stalled.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Stalled (active stage &gt; 30 days)</div>
          <ul className="space-y-1 text-[12px]">
            {p.stalled.map(s => (
              <li key={s.name} className="flex justify-between">
                <span className="text-navy">{s.name}</span>
                <span className="text-[#888]">{s.stage} · {s.days}d</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

function ClientsSection({ data }) {
  const c = data.clients;
  return (
    <SectionCard title="Clients — BWM" subtitle="Net new · churn callouts">
      <div className="grid grid-cols-4 gap-4 text-[13px]">
        <Stat label="Active" value={c.activeCount} />
        <Stat label="Added" value={c.added} delta={pctDelta(c.added, c.addedPrior)} subtitle={`Prior: ${c.addedPrior}`} accent={c.added > 0 ? 'text-deep-teal' : 'text-navy'} />
        <Stat label="Churned" value={c.churned} delta={pctDelta(c.churned, c.churnedPrior)} subtitle={`Prior: ${c.churnedPrior}`} accent={c.churned > 0 ? 'text-rose' : 'text-[#888]'} />
        <Stat label="Net Change" value={c.netChange >= 0 ? `+${c.netChange}` : c.netChange} subtitle={`Prior: ${c.priorNetChange >= 0 ? '+' : ''}${c.priorNetChange}`} accent={c.netChange >= 0 ? 'text-deep-teal' : 'text-rose'} />
      </div>

      {c.addedList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Added this month</div>
          <ul className="space-y-1 text-[12px]">
            {c.addedList.map(client => (
              <li key={client.id} className="flex justify-between">
                <span className="text-navy">{client.name}</span>
                <span className="text-[#888]">{client.serviceTier || ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {c.churnedList.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Churned this month</div>
          <ul className="space-y-1 text-[12px]">
            {c.churnedList.map(client => (
              <li key={client.id} className="flex justify-between">
                <span className="text-navy">{client.name}</span>
                <span className="text-[#888]">{client.churnedDate} · {client.serviceTier || ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

function InsuranceSection({ data }) {
  // v1: minimal placeholder. Will enrich in a later iteration once the
  // /api/insurance/policies endpoint surface for monthly stats is finalized.
  return (
    <SectionCard title="Insurance (BIA)" subtitle="Renewals + new bindings · Coming in v2">
      <div className="text-[12px] text-[#888]">
        Detailed monthly insurance stats (renewals due, new bindings, lapses)
        will land in the next report iteration. For now, see <a href="/insurance" className="text-bullogic-blue hover:underline">/insurance</a>
        for the live policies dashboard.
      </div>
    </SectionCard>
  );
}

// ─── Trajectory section: 6-month mini-sparklines ───────────────────
//
// One row per metric. Each row has the label + current value + tiny
// inline SVG sparkline. Goal: show the SHAPE of the trend without
// requiring an axis read. Direction matters more than absolute value.
function TrajectorySection({ data }) {
  const t = data.trajectory;
  if (!t) return null;
  const cur = (arr) => arr[arr.length - 1];

  const rows = [
    { label: 'BWM Revenue',    series: t.revenueSeries,      format: (v) => formatCurrency(Math.round(v)) },
    { label: 'Spend % YTD',    series: t.spendPctSeries,     format: (v) => `${(v * 100).toFixed(1)}%` },
    { label: 'Anchors / mo',   series: t.anchorsSeries,      format: (v) => `${v}` },
    { label: 'New Prospects',  series: t.newProspectsSeries, format: (v) => `${v}` },
    { label: 'Closed Won',     series: t.wonSeries,          format: (v) => `${v}` },
  ];

  return (
    <SectionCard title="6-Month Trajectory" subtitle="Direction matters more than the snapshot. Last 6 months including this one.">
      <div className="space-y-2.5">
        {rows.map(r => (
          <div key={r.label} className="grid grid-cols-12 items-center gap-3 text-[12px]">
            <div className="col-span-3 text-[#666]">{r.label}</div>
            <div className="col-span-7"><Sparkline values={r.series} /></div>
            <div className="col-span-2 text-right tabular-nums font-bold text-navy">{r.format(cur(r.series))}</div>
          </div>
        ))}
      </div>
      {/* X-axis labels */}
      <div className="grid grid-cols-12 gap-3 mt-2">
        <div className="col-span-3" />
        <div className="col-span-7 flex justify-between text-[9px] text-[#aaa]">
          {t.labels.map((l, i) => <span key={i}>{l}</span>)}
        </div>
        <div className="col-span-2" />
      </div>
    </SectionCard>
  );
}

// Tiny inline-SVG sparkline. No axes, no chart library. Auto-scales
// to the value range; flat series renders as a flat line. Final point
// gets a small dot so the eye anchors on "now."
function Sparkline({ values, height = 24 }) {
  if (!Array.isArray(values) || values.length === 0) return null;
  const W = 100; // arbitrary viewBox width — scales to container
  const H = height;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = values.length > 1 ? W / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = i * stepX;
    // Pad 2px top + bottom so the line never touches the edge
    const y = H - 2 - ((v - min) / range) * (H - 4);
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: H }}>
      <path d={path} fill="none" stroke="#2596BE" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
      {last && <circle cx={last[0]} cy={last[1]} r="1.6" fill="#2596BE" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

function Stat({ label, value, subtitle, delta, accent = 'text-navy' }) {
  let deltaStr = null;
  let deltaCls = 'text-[#888]';
  if (delta !== undefined && delta !== null && Number.isFinite(delta)) {
    const pct = (delta * 100).toFixed(0);
    deltaStr = `${delta >= 0 ? '+' : ''}${pct}% vs prior`;
    deltaCls = delta >= 0 ? 'text-deep-teal' : 'text-rose';
  }
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#888]">{label}</div>
      <div className={`text-[24px] font-bold leading-tight ${accent}`}>{value}</div>
      {subtitle && <div className="text-[10px] text-[#888] mt-0.5">{subtitle}</div>}
      {deltaStr && <div className={`text-[10px] mt-0.5 ${deltaCls}`}>{deltaStr}</div>}
    </div>
  );
}
